import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../api';
import { storeFromSlug, isKnownStoreSlug, KNOWN_STORES } from '../utils/slug';
import { useWishlist } from '../utils/wishlist';
import OfferCard from '../components/common/OfferCard';
import NotFound from './NotFound';

const STORE_META = {
  Flipkart: {
    color: '#2874F0',
    accent: '#FFE500',
    tagline: 'Big Billion Days, Mobile & Electronics Deals',
    intro: "Find today's verified Flipkart offers across mobiles, electronics, fashion and home. Whether it's the Big Billion Days sale, Big Saving Days or daily lightning deals, every Flipkart coupon and bank cashback offer on OfferLelo is hand-checked before listing.",
    highlights: [
      'Big Billion Days & Big Saving Days sale offers',
      'Mobile launch offers with bank cashback (HDFC, SBI, ICICI, Axis)',
      'No-cost EMI deals on electronics',
      'Daily lightning deals & exchange offers',
    ],
  },
  Amazon: {
    color: '#FF9900',
    accent: '#232F3E',
    tagline: 'Great Indian Sale, Prime Day & Lightning Deals',
    intro: "Today's best Amazon India offers, from the Great Indian Festival and Prime Day to daily Lightning Deals. Each Amazon coupon code, bank instant discount and Prime member offer on OfferLelo is verified before publishing so you can shop with confidence.",
    highlights: [
      'Great Indian Festival & Prime Day sale offers',
      'Lightning Deals refreshed every few hours',
      'Bank instant discounts (HDFC, SBI, ICICI, Amazon Pay ICICI)',
      'Coupon codes & no-cost EMI offers',
    ],
  },
  Myntra: {
    color: '#FF3F6C',
    accent: '#282C3F',
    tagline: 'End of Reason Sale, Fashion & Beauty Deals',
    intro: "Explore the latest Myntra offers on fashion, footwear, beauty and accessories. From the famous End of Reason Sale to flash discounts on top brands, every Myntra coupon and bank offer on OfferLelo is verified daily to help you save more on every order.",
    highlights: [
      'End of Reason Sale (EORS) deals',
      'Up to 70% off on top fashion brands',
      'Beauty & cosmetics offers (Maybelline, Lakmé, MAC)',
      'Bank discounts on Kotak, HDFC, ICICI cards',
    ],
  },
  Ajio: {
    color: '#2D2926',
    accent: '#C9A24A',
    tagline: 'Big Bold Sale, Fashion & Trend Deals',
    intro: "Browse today's verified Ajio offers on western wear, ethnic wear, footwear and accessories. From the Big Bold Sale to daily trend-led discounts and exclusive coupon codes, every Ajio deal on OfferLelo is hand-checked so you don't waste time on expired offers.",
    highlights: [
      'Big Bold Sale & Republic Day Sale offers',
      'Up to 90% off on fashion & footwear',
      'Coupon codes for new users & existing customers',
      'Bank offers on SBI, ICICI, HDFC cards',
    ],
  },
  Others: {
    color: '#6366F1',
    accent: '#8B5CF6',
    tagline: 'Deals & Discounts from Other Top Stores',
    intro: "Discover verified offers from other popular Indian retailers including Tata CLiQ, Croma, Reliance Digital, Nykaa, Meesho, FirstCry and more. Every coupon, sale and cashback deal listed here is hand-checked daily so you can grab the latest discounts beyond the big four stores.",
    highlights: [
      'Tata CLiQ, Croma & Reliance Digital electronics deals',
      'Nykaa beauty & cosmetics offers',
      'Meesho, FirstCry & specialty store discounts',
      'Credit card cashback & bank offers across stores',
    ],
  },
};

// Module-level cache keyed by storeSlug so each store page survives remounts.
const storePageCache = new Map();

export default function StorePage() {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useWishlist();

  const storeName = storeFromSlug(storeSlug);
  const meta = STORE_META[storeName];
  const cached = storePageCache.get(storeSlug);

  const [items, setItems] = useState(() => cached?.items ?? []);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => cached?.lastEvaluatedKey ?? null);
  const [hasMore, setHasMore] = useState(() => cached?.hasMore ?? true);
  const [loading, setLoading] = useState(() => !cached);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  const hadCacheOnMount = useRef(!!cached);

  // Persist current state to cache on unmount (so back-nav restores it)
  useEffect(() => {
    return () => {
      if (isKnownStoreSlug(storeSlug)) {
        storePageCache.set(storeSlug, { items, lastEvaluatedKey, hasMore });
      }
    };
  });

  useEffect(() => {
    if (!isKnownStoreSlug(storeSlug)) return;
    // Skip the initial fetch if we restored from cache for this store
    if (hadCacheOnMount.current) {
      hadCacheOnMount.current = false;
      return;
    }
    setItems([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
    fetchStoreOffers(null, true);
  }, [storeSlug]);

  async function fetchStoreOffers(lek, replace = false) {
    replace ? setLoading(true) : setLoadingMore(true);
    setError(null);
    const isOthers = storeName === 'Others';
    try {
      const params = new URLSearchParams({ limit: 6 });
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      // Named stores → GSI-backed endpoint. Others → generic offers + client-side exclude.
      const url = isOthers
        ? `${API_ENDPOINTS.OFFERS}?${params}`
        : `${API_ENDPOINTS.OFFERS_BY_STORE}/${encodeURIComponent(storeName)}?${params}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch offers');
      const data = await response.json();
      let list = data.offers || data;
      if (Array.isArray(list) && isOthers) {
        const known = KNOWN_STORES.map(s => s.toLowerCase());
        list = list.filter(o => !o.store || !known.includes(o.store.toLowerCase()));
      }
      setItems(prev => replace ? list : [...prev, ...list]);
      setLastEvaluatedKey(data.lastEvaluatedKey || null);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      setError(err.message);
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchStoreOffers(lastEvaluatedKey);
        }
      },
      { threshold: 0, rootMargin: '600px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, lastEvaluatedKey]);

  if (!isKnownStoreSlug(storeSlug)) {
    return <NotFound />;
  }

  const pageUrl = `https://www.offerlelo.com/store/${storeSlug.toLowerCase()}`;
  const isOthers = storeName === 'Others';
  const pageTitle = isOthers
    ? 'More Store Offers — Tata CLiQ, Croma, Nykaa & More | OfferLelo'
    : `${storeName} Offers Today — Latest Deals, Coupons & Discounts | OfferLelo`;
  const heroH1 = isOthers
    ? "More Store Offers — Today's Latest Deals & Discounts"
    : `${storeName} Offers — Today's Latest Deals & Discounts`;
  const heroBadge = isOthers ? 'MORE STORES' : `${storeName.toUpperCase()} STORE`;
  const sectionH2 = isOthers ? 'Latest Offers from Other Stores' : `Latest ${storeName} Offers`;
  const emptyText = isOthers
    ? 'No offers from other stores right now — check back soon.'
    : `No ${storeName} offers right now — check back soon.`;
  const loadingMoreText = isOthers ? 'Loading more offers...' : `Loading more ${storeName} offers...`;
  const pageDesc = `${meta.intro.slice(0, 155)}`;

  return (
    <div className="App">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content={`${storeName} offer, ${storeName} deal, ${storeName} coupon, ${storeName} discount, ${storeName} sale today, ${storeName} cashback offer, online shopping India`} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="IN" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content="https://www.offerlelo.com/logo192.png" />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://www.offerlelo.com/logo192.png" />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${storeName} Offers — Latest Deals & Discounts`,
          "url": pageUrl,
          "description": pageDesc,
          "inLanguage": "en-IN",
          "isPartOf": { "@type": "WebSite", "url": "https://www.offerlelo.com", "name": "OfferLelo" },
          "about": { "@type": "Organization", "name": storeName },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.offerlelo.com" },
              { "@type": "ListItem", "position": 2, "name": "Stores", "item": "https://www.offerlelo.com/offers" },
              { "@type": "ListItem", "position": 3, "name": `${storeName} Offers`, "item": pageUrl }
            ]
          }
        })}</script>
      </Helmet>

      <header className="modern-header">
        <div className="header-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <button className="admin-btn" onClick={() => navigate('/')}>← Home</button>
        </div>
      </header>

      {/* Hero — store branded */}
      <section
        style={{
          background: `linear-gradient(135deg, ${meta.color} 0%, ${meta.accent} 100%)`,
          padding: '48px 20px 40px',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.18)', padding: '6px 14px',
            borderRadius: 999, fontSize: '0.82rem', fontWeight: 600, marginBottom: 12, letterSpacing: 0.4,
          }}>
            ✨ {heroBadge}
          </div>
          <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 800, margin: '0 0 10px', lineHeight: 1.2 }}>
            {heroH1}
          </h1>
          <p style={{ fontSize: '1.02rem', opacity: 0.95, margin: '0 auto', maxWidth: 720, lineHeight: 1.55 }}>
            {meta.tagline}
          </p>
        </div>
      </section>

      {/* Real content paragraph (not keyword stuffed — gives Google a reason to rank) */}
      <section style={{ maxWidth: 900, margin: '28px auto 8px', padding: '0 20px' }}>
        <p style={{ fontSize: '0.98rem', color: '#475569', lineHeight: 1.7, margin: 0 }}>
          {meta.intro}
        </p>
        <ul style={{ marginTop: 16, paddingLeft: 22, color: '#475569', lineHeight: 1.8, fontSize: '0.95rem' }}>
          {meta.highlights.map(h => <li key={h}>{h}</li>)}
        </ul>
      </section>

      <h2 style={{ maxWidth: 1400, margin: '32px auto 12px', padding: '0 20px', fontSize: '1.3rem', color: '#1e293b' }}>
        {sectionH2}
      </h2>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 12px 60px' }}>
        {loading && (
          <div className="grid-container">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton skeleton-card-img" />
                <div className="skeleton-card-body">
                  <div className="skeleton skeleton-card-line wide" />
                  <div className="skeleton skeleton-card-line mid" />
                  <div className="skeleton skeleton-card-line short" />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && !loading && (
          <div style={{ color: '#ef4444', textAlign: 'center', padding: 20 }}>Error: {error}</div>
        )}
        {!loading && !error && (
          <>
            <div className="grid-container">
              {items.length === 0 ? (
                <div className="no-items">
                  {emptyText}
                </div>
              ) : (
                items.map(item => (
                  <OfferCard
                    key={item.id}
                    item={item}
                    savedIds={savedIds}
                    onToggleSave={toggleSave}
                  />
                ))
              )}
            </div>
            <div ref={sentinelRef} style={{ height: 32 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: meta.color, fontWeight: 600, fontSize: '0.95rem' }}>
                {loadingMoreText}
              </div>
            )}
          </>
        )}

        {/* Internal cross-link nav to other store pages (excludes "Others") */}
        <section style={{ marginTop: 48, padding: '24px 20px', background: '#f8fafc', borderRadius: 12 }}>
          <h2 style={{ fontSize: '1.1rem', color: '#1e293b', margin: '0 0 14px' }}>Explore Other Stores</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.keys(STORE_META)
              .filter(s => s !== storeName && s !== 'Others')
              .map(s => (
                <button
                  key={s}
                  onClick={() => navigate(`/store/${s.toLowerCase()}`)}
                  style={{
                    background: '#fff', border: `1.5px solid ${STORE_META[s].color}40`, color: STORE_META[s].color,
                    padding: '10px 18px', borderRadius: 999, fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer',
                  }}
                >
                  {s} Offers →
                </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
